/**
 * Tenant currency card (DNKADM-0050) — the setup-only currency editor for
 * `PUT /v2/admin/tenant-settings/currency` (handoff §5.1).
 *
 * Two states, gated by the lock signal:
 *  - **Unlocked (setup):** an ISO-4217 currency selector + an optional "convert
 *    existing prices" control (a `Current:New` rate, e.g. `500:1`). "Change
 *    currency" → `PUT …/currency`; on success the parent `refetch`es and a success
 *    toast is raised.
 *  - **Locked:** read-only — the current currency + an explanatory note that it
 *    locked on the first reservation/payment and can no longer be changed.
 *
 * **Lock detection (§5.1 assumption):** the card is locked when `data.currencyLocked
 * === true` (the assumed GET signal) OR after a `409 CURRENCY_LOCKED` is returned by
 * the currency PUT (the authoritative fallback). On that `409` the card flips to the
 * locked state immediately (a local `locked` flag) AND `refetch`es, and raises an
 * explanatory toast — it never crashes. Other failures surface as an error toast.
 *
 * Currency is read-only on the MAIN settings PUT (a separate card); it is changed
 * ONLY here, only while unlocked.
 */
import { useId, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import type { ShowToast } from '@/components/toastContext';
import {
  isCurrencyLockedError,
  putTenantCurrency,
  type TenantSettingsView,
} from '@/api/tenantSettings';
import { serverMessage } from '@/api/serverMessage';
import { CURRENCY_OPTIONS, currencyOptionFor } from './currencies';

interface CurrencyCardProps {
  /** The loaded settings (carries the current `currency` + the lock signal). */
  data: TenantSettingsView;
  /** Re-fetch after a successful change / on a lock transition. */
  refetch: () => void;
  /** Raise a non-blocking toast (success / lock / upstream-error). */
  showToast: ShowToast;
}

/**
 * Validate the optional conversion rate. Empty is allowed (no conversion). A
 * provided rate must be `Current:New` with two positive numbers (e.g. `500:1`).
 */
function validateRate(raw: string): { ok: true } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true };
  const m = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(trimmed);
  if (!m) return { ok: false, message: 'Enter a rate as Current:New (e.g. 500:1).' };
  const current = Number(m[1]);
  const next = Number(m[2]);
  if (!(current > 0) || !(next > 0)) {
    return { ok: false, message: 'Both sides of the rate must be greater than 0.' };
  }
  return { ok: true };
}

export function CurrencyCard({ data, refetch, showToast }: CurrencyCardProps) {
  const ids = useId();

  // Local lock flag layered over the GET signal: a `409 CURRENCY_LOCKED` flips it
  // true even when the GET reported the tenant as unlocked (the §5.1 fallback).
  const [lockedByServer, setLockedByServer] = useState(false);
  const locked = data.currencyLocked === true || lockedByServer;

  const [currency, setCurrency] = useState(data.currency);
  const [convertPrices, setConvertPrices] = useState(false);
  const [conversionRate, setConversionRate] = useState('');
  const [rateError, setRateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const currencyId = `${ids}-currency`;
  const rateId = `${ids}-rate`;

  async function handleChange() {
    setRateError(null);
    if (convertPrices) {
      const v = validateRate(conversionRate);
      if (!v.ok) {
        setRateError(v.message);
        return;
      }
    }
    setSaving(true);
    try {
      await putTenantCurrency({
        currency,
        // Only carry the conversion fields when the operator opted in.
        ...(convertPrices
          ? { convertPrices: true, conversionRate: conversionRate.trim() }
          : {}),
      });
      refetch();
      showToast('Currency updated', 'success');
    } catch (err) {
      if (isCurrencyLockedError(err)) {
        // The currency locked between load and save — flip to locked, refetch the
        // (now-authoritative) state, and explain. Never crash.
        setLockedByServer(true);
        refetch();
        showToast(
          'Currency is locked — the tenant already has financial history, so it can no longer be changed.',
          'error',
        );
      } else {
        showToast(serverMessage(err) ?? 'The currency couldn’t be changed. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            p: '16px 20px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'background.default',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <Icon name="payments" sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Currency</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
              The ISO currency for all prices and payments at this tenant.
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ p: '18px 20px' }}>
          {locked ? (
            <LockedCurrency code={data.currency} />
          ) : (
            <Stack spacing={2}>
              <TextField
                id={currencyId}
                select
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={saving}
                sx={{ width: 280 }}
                slotProps={{ htmlInput: { 'aria-label': 'Currency' } }}
              >
                {/* If the current (server-set) currency is off the curated list, it
                    is still selectable so the field always reflects the real value. */}
                {(CURRENCY_OPTIONS.some((c) => c.code === currency)
                  ? CURRENCY_OPTIONS
                  : [currencyOptionFor(currency), ...CURRENCY_OPTIONS]
                ).map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.code} — {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={convertPrices}
                      onChange={(e) => setConvertPrices(e.target.checked)}
                      disabled={saving}
                    />
                  }
                  label="Convert existing prices"
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 4, mt: -0.5 }}>
                  Multiply all configured prices by the new/current rate when switching.
                </Typography>
                {convertPrices && (
                  <TextField
                    id={rateId}
                    label="Conversion rate (Current:New)"
                    value={conversionRate}
                    onChange={(e) => setConversionRate(e.target.value)}
                    disabled={saving}
                    placeholder="500:1"
                    error={Boolean(rateError)}
                    helperText={rateError ?? 'e.g. 500:1 — 500 old units become 1 new unit.'}
                    sx={{ width: 280, mt: 1.5, ml: 4 }}
                    slotProps={{ htmlInput: { 'aria-label': 'Conversion rate' } }}
                  />
                )}
              </Box>

              <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => void handleChange()}
                  disabled={saving}
                >
                  {saving ? 'Changing…' : 'Change currency'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

/** Read-only locked-currency presentation + explanation. */
function LockedCurrency({ code }: { code: string }) {
  const { label } = currencyOptionFor(code);
  return (
    <Stack spacing={1.25}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Icon name="lock" sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden />
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
          {code}
          {label !== code ? ` — ${label}` : ''}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        The currency is locked. It was set during setup and locked on the first reservation or
        payment, so it can no longer be changed.
      </Typography>
    </Stack>
  );
}
